import { motion } from "framer-motion";
import { Compass, ListChecks, Puzzle, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticLines from "@/components/KineticLines";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { IMAGES, WHATSAPP_LINK } from "@/lib/constants";

const PRINCIPIOS = [
    {
        icon: Compass,
        titulo: "Análise da realidade",
        texto: "Nosso trabalho parte de uma análise da realidade de cada negócio — contexto, operação e mercado.",
    },
    {
        icon: ListChecks,
        titulo: "Prioridades claras",
        texto: "Identificamos oportunidades e definimos o que vem primeiro, com foco no que gera evolução concreta.",
    },
    {
        icon: Puzzle,
        titulo: "Soluções aplicáveis",
        texto: "Desenvolvemos soluções que cabem na rotina da empresa — sem fórmulas prontas.",
    },
];

const QuemSomos = () => (
    <main>
        <Seo
            title="Quem Somos — Vorantis Soluções"
            description="A Vorantis Soluções atua ao lado de empresas que desejam organizar a operação, fortalecer a presença no mercado e crescer com direção. Barra Mansa/RJ."
        />

        <section className="grain-overlay hero-grid-bg relative overflow-hidden bg-navy-deep pb-24 pt-40 md:pb-32 md:pt-48" data-testid="quem-somos-hero">
            <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-corp/20 blur-[140px]" />
            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <motion.div
                        className="flex items-center gap-3"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                    >
                        <span className="h-px w-10 bg-emerald-400" />
                        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-400">Quem somos</span>
                    </motion.div>
                    <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl">
                        <KineticLines
                            testid="quem-somos-headline"
                            delay={0.3}
                            lines={["Evolução", <>com <span className="text-emerald-400">direção.</span></>]}
                        />
                    </h1>
                    <motion.p
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.85 }}
                    >
                        A Vorantis Soluções atua ao lado de empresas que desejam organizar sua
                        operação, fortalecer sua presença no mercado e construir caminhos
                        sustentáveis de crescimento.
                    </motion.p>
                </div>
                <motion.div
                    className="relative lg:col-span-5"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.5 }}
                >
                    <div className="absolute -left-3 -top-3 h-full w-full rounded-3xl border border-emerald-500/40" aria-hidden="true" />
                    <img
                        src={IMAGES.team_collaboration}
                        alt="Equipe Vorantis em sessão de trabalho estratégico com cliente"
                        className="relative aspect-[4/3] w-full rounded-3xl object-cover"
                    />
                </motion.div>
            </div>
        </section>

        <section className="bg-slate-50 py-24 md:py-32" data-testid="filosofia-section">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <SectionHeading
                    kicker="Filosofia de trabalho"
                    title={<>Compreender a realidade <span className="text-corp">antes de propor soluções.</span></>}
                    description="Nosso trabalho parte de uma análise da realidade de cada negócio. Identificamos oportunidades, definimos prioridades e desenvolvemos soluções aplicáveis — sem fórmulas prontas."
                />
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {PRINCIPIOS.map((p, i) => (
                        <Reveal key={p.titulo} delay={i * 0.1}>
                            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 transition-shadow duration-300 hover:shadow-[0_24px_48px_-28px_rgba(10,25,47,0.3)]">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy-deep text-emerald-400">
                                    <p.icon size={22} />
                                </div>
                                <h3 className="mt-6 font-display text-lg font-bold text-slate-900">{p.titulo}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.texto}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="grain-overlay bg-navy-deep py-24 md:py-32" data-testid="visao-integrada-section">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <SectionHeading dark kicker="Uma visão integrada" title="Por dentro e por fora, em movimento." />
                <div className="mt-14 grid gap-6 md:grid-cols-2">
                    <Reveal>
                        <div className="relative h-full overflow-hidden rounded-3xl border border-white/10 bg-navy-surface p-9 md:p-12">
                            <span className="font-mono text-xs font-bold tracking-[0.3em] text-emerald-400">01</span>
                            <h3 className="mt-5 font-display text-2xl font-bold text-white md:text-3xl">Gestão eficiente por dentro.</h3>
                            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300 md:text-base">
                                Processos claros, indicadores relevantes e rotinas padronizadas para a
                                empresa decidir com clareza e operar com consistência.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="relative h-full overflow-hidden rounded-3xl border border-white/10 bg-navy-surface p-9 md:p-12">
                            <span className="font-mono text-xs font-bold tracking-[0.3em] text-emerald-400">02</span>
                            <h3 className="mt-5 font-display text-2xl font-bold text-white md:text-3xl">Posicionamento consistente por fora.</h3>
                            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300 md:text-base">
                                Presença digital organizada para ser encontrada, gerar confiança e
                                transformar canais em conversas e propostas.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        <section className="relative overflow-hidden py-24 md:py-32" data-testid="localizacao-section">
            <img
                src={IMAGES.office_architectural}
                alt="Arquitetura corporativa moderna na região de atuação da Vorantis"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-navy-deep/85" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <Reveal>
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-3">
                            <MapPin size={18} className="text-emerald-400" />
                            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-400">Onde estamos</span>
                        </div>
                        <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            Barra Mansa • RJ
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
                            Atendemos empresas de Barra Mansa e de todo o Sul Fluminense — perto da
                            sua operação, com a visão de quem entende o mercado regional.
                        </p>
                        <a
                            href={WHATSAPP_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="localizacao-whatsapp"
                            className="mt-9 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-4 font-semibold text-white transition-colors hover:bg-emerald-600"
                        >
                            Conversar com a Vorantis
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>

        <CTABand />
    </main>
);

export default QuemSomos;
